import { bannerMsgR2 } from 'services/graphql/fragments/BannerMessagesR2';
import {
  card,
  creditScore,
  money,
  savingsTracker,
  TransactionRowFragment,
} from 'services/graphql/fragments/Fragments';
import { AnalyticsFragment } from 'services/graphql/fragments/VxlFragments';
import {
  IconFragment,
  VXLResponsiveTextFragment,
} from 'services/graphql/fragments/VxlStyles';
import { gql } from 'urql';

const gqlQuery = gql`
  query AccountPageTile($id: ID!, $input: InternalAccountPageTileInput!) {
    accountR2(id: $id) {
      id
      type
      __typename
      type
      product
      status
      name
      nickname
      iconUrl
      accountNumber
      accountNumberLast4
      institution {
        name
      }
      bannerMessagesR2 {
        ...bannerMsgR2
      }
      ... on InternalCheckingAccount {
        routingNumber
        isDefaultAccount
        cards {
          ...card
        }
        displayBalance {
          title
          amount {
            ...money
          }
        }
      }
      ... on InternalSavingsAccount {
        apy
        yearToDateInterest {
          ...money
        }
        isDefaultAccount
        routingNumber
        displayBalance {
          title
          amount {
            ...money
          }
        }
        savingsTracker {
          ...savingsTracker
        }
        details {
          items {
            key
            itemDetail {
              title
              descriptionHtml
              articleLink {
                __typename
                copy
                articleId
                article {
                  id
                  title
                }
              }
            }
          }
        }
      }
      ... on InternalAdvanceAccount {
        id
        type
        product
        status
        advanceStatus
        name
        nickname
        iconUrl
        accountNumber
        accountNumberLast4
        routingNumber
        isDefaultAccount
        reEligiblityDetail {
          description
          descriptionHtml
          title
        }
        totalBalance {
          ...money
        }
        availableBalance {
          ...money
        }
        displayBalance {
          title
          amount {
            ...money
          }
        }
        description
        activeAdvance {
          id
          description
          dateOfAdvance
          dueDate
          canRepay
          advancePayments {
            ... on SLTitledListComponent {
              id
              icon {
                ...IconFragment
              }
              title {
                ...VXLResponsiveTextFragment
              }
              children {
                __typename
                ...TransactionRowFragment
              }
            }
          }
          amountDue {
            ...money
          }
          details {
            items {
              title
              description
              copy
            }
          }
          detailsV2 {
            items {
              copy
              vxlCopy {
                ...VXLResponsiveTextFragment
              }
              title
              id
              description
              vxlTitle {
                ...VXLResponsiveTextFragment
              }
            }
          }
        }
        agreements {
          id
          title
          documentId
        }
        pastAdvances {
          edges {
            node {
              __typename
              id
              description
              dateOfAdvance
              dueDate
              amount {
                ...money
              }
              fee {
                ...money
              }
              amountDue {
                ...money
              }
              detailsV2 {
                items {
                  copy
                  vxlCopy {
                    ...VXLResponsiveTextFragment
                  }
                  title
                  id
                  description
                  vxlTitle {
                    ...VXLResponsiveTextFragment
                  }
                }
              }
              payments {
                id
                date
                amount {
                  ...money
                }
                title
                description
              }
            }
          }
        }
      }
      ... on InternalChargeCardAccount {
        id
        ...creditScore
        routingNumber
        isDefaultAccount
        hasVaultShortfall
        totalBalance {
          ...money
        }
        availableBalance {
          ...money
        }
        cards {
          ...card
        }
        isSafePayEnabled
        autoPaymentSettings {
          enabled
        }
        action {
          type
          title
          message
          location
        }
        vaultBalance {
          ...money
        }
        paymentDue {
          dueDate
          amount {
            ...money
          }
        }
        vaultShortFallAmount {
          ...money
        }
        canAddFunds
        canPayCard
        displayBalance {
          title
          amount {
            ...money
          }
        }
      }
      ... on ExternalPlaidAccount {
        verificationInfo {
          message
          details
        }
        createdAt
        routingNumber
        lastUpdated
        displayBalance {
          title
          amount {
            ...money
          }
        }
      }
    }
    accountPageTile(input: $input) {
      id
      accountId
      status
      tileType
      title
      subtitle
      navigation {
        id
        pills {
          id
          title {
            ...VXLResponsiveTextFragment
          }
          onClick {
            ... on SmartViewNavigationAction {
              accountId
              categoryId
              analytics {
                ...analytics
              }
            }
          }
        }
        displayAnalytics {
          ...analytics
        }
      }
      tiles {
        id
        status
        __typename
        tileType
        ... on AccountDetailsLinkTile {
          id
          tileType
          accountId
          accountDetailsLinkTileType
          title
          status
          __typename
        }
        ... on EmptyStateContentTile {
          id
          tileType
          emptyStateContentTileType
          heading
          caption
          __typename
        }
        ... on AddMoneyTile {
          id
          tileType
          addMoneyTileType
          cta
          __typename
        }
        ... on ListTile {
          status
          listTitle
          listTileType
          tileType
          __typename
          tiles {
            ... on QuickActionTile {
              id
              tileType
              quickActionTileType
              accountId
              title
              __typename
            }
            ... on ReferralBannerTile {
              id
              tileType
              referralBannerTileType
              message
              zendeskId
              __typename
            }
            ... on NotificationBannerTile {
              id
              notificationBannerTileType
              message
              color
              __typename
            }
            ... on AccountCopyAttributeTile {
              id
              title
              accountId
              tileType
              __typename
              accountCopyAttributeTileType
              copy
              secondaryCopy
            }
            ... on AccountToggleAttributeTile {
              id
              title
              tileType
              accountToggleAttributeTileType
              isOn
              isOnCopy
              accountId
              __typename
            }
            ... on AccountAttributeTile {
              id
              tileType
              __typename
              accountId
              accountAttributeTileType
              attribute {
                ... on CardBalanceAttribute {
                  title
                  balance
                }
              }
            }
            ... on AccountUpDownAttributeTile {
              id
              title
              accountUpDownAttributeTileType
              accountId
              tileType
              __typename
              upDownValue
              direction
            }
            ... on ContentCtaTile {
              id
              title
              description
              tileType
              __typename
              contentCtaTileType
              status
              ctaTag {
                __typename
                text
                color
              }
            }
          }
        }
      }
    }
  }
  ${creditScore}
  ${money}
  ${savingsTracker}
  ${bannerMsgR2}
  ${VXLResponsiveTextFragment}
  ${AnalyticsFragment}
  ${IconFragment}
  ${TransactionRowFragment}
  ${card}
`;

export default gqlQuery;
